import { Device } from '@capacitor/device'
import { useActorMember, useAxios, useSubjectMember } from '@healthblocks-io/core'
import { t } from '@lingui/macro'
import { isNil } from 'ramda'
import { useCallback, useEffect, useState } from 'react'
import useSWR from 'swr'
import { v4 as uuidv4 } from 'uuid'

import { useAxiosAsync } from '@/@bothrs-react/hooks/useAxiosAsync'
import { useIntegrationAxios } from '@/modules/healthblocks/hooks/useIntegrationAxios'
import { getLatestMessageFromList, getLatestNonPatientSenderFromList } from '@/modules/messages/utils/messageOrder'

import type { DeviceInfo } from '@capacitor/device'
import type { ESignDoctor } from '@/modules/appointment/types/e-sign.types'
import type {
  PatientCommunication,
  PatientCommunicationChannels,
  PatientCommunicationMessage,
  PatientCommunicationSpecialisation,
  PatientCommunicationTarget,
} from '../types/patient-communication.type'

export function usePatientCommunication() {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>()

  const axios = useIntegrationAxios()
  const healthblocksAxios = useAxios()

  const actorMember = useActorMember()
  const member = useSubjectMember()

  // Effect
  // ------------------------------------------------------------------------- /
  useEffect(() => {
    Device.getInfo().then((device) => {
      setDeviceInfo(device)
    })
  }, [])

  const getCommunicationChannels = useCallback(() => {
    return axios.get<PatientCommunicationChannels>('/synops/get-communication-channels', {
      params: { synopsPatientId: member?.id },
    })
  }, [axios, member?.id])

  const getPatientCommunication = useCallback(() => {
    return healthblocksAxios.get<PatientCommunication[]>('communication', {
      params: {
        subjectId: member?.id,
        actorRole: ['subject'].join(','),
      },
    })
  }, [healthblocksAxios, member?.id])

  const createPatientCommunication = useCallback(
    (
      title: string,
      message: string,
      category: string,
      receiverType: PatientCommunicationTarget['type'] = 'doctor',
      specialisation?: PatientCommunicationSpecialisation,
      doctor?: ESignDoctor,
    ) => {
      const receiver: PatientCommunicationTarget = {
        name:
          doctor?.name ||
          specialisation?.title?.nl ||
          t({
            id: 'globals.azmm-contact.name',
            comment: 'General contact info name of AZMM',
            message: 'AZ Maria Middelares',
          }),
        type: receiverType as 'doctor' | 'hospital',
        userId: doctor?.id ?? 0,
        specialisationId: specialisation?.agendaId ? Number(specialisation.agendaId) : undefined,
      }
      // Create new Patient communication base
      const now = new Date().toISOString()

      const content: Partial<PatientCommunication> = {
        type: 'PatientCommunication',
        actorRole: 'subject',
        status: 'scheduled',
        display: title,
        plannedAt: now,
        subjectId: member?.id,
        meta: {
          category,
          messages: [
            {
              id: uuidv4(),
              sender: {
                name: `${member?.givenName} ${member?.familyName}`,
                type: 'patient',
                actorId: actorMember?.id,
                id: member?.id,
              } as PatientCommunicationTarget,
              receiver: receiver,
              content: message,
              createdAt: now,
              hidden: false,
              readStatus: { patient: now },
            },
          ],
          appVersion: APP_VERSION,
          deviceInfo,
        },
      }

      return healthblocksAxios.post<PatientCommunication>('/communication', content)
    },
    [healthblocksAxios, deviceInfo, member?.familyName, member?.givenName, member?.id, actorMember?.id],
  )

  const replyToPatientCommunication = useCallback(
    (communication: PatientCommunication, message: string) => {
      const messagesByDateAsc = [...(communication?.meta?.messages || [])].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      )

      const latestNonPatientMessage = messagesByDateAsc.filter((m) => m.sender.type !== 'patient').at(-1)
      const receiver = latestNonPatientMessage?.sender || messagesByDateAsc.at(0)?.receiver

      const now = new Date().toISOString()
      const newMessage: PatientCommunicationMessage = {
        id: uuidv4(),
        sender: {
          name: `${member?.givenName} ${member?.familyName}`,
          type: 'patient',
          actorId: actorMember?.id ?? 0,
          id: member?.id,
        },
        receiver: receiver || {
          name: '',
          type: 'doctor',
        },
        content: message,
        createdAt: now,
        hidden: false,
        readStatus: { patient: now },
      }

      return healthblocksAxios.post<PatientCommunication>(`/communication/${communication.id}/messages`, newMessage)
    },
    [healthblocksAxios, member?.familyName, member?.givenName, actorMember?.id, member?.id],
  )

  return {
    getCommunicationChannels: useAxiosAsync(getCommunicationChannels),
    getPatientCommunication: useAxiosAsync(getPatientCommunication),
    createPatientCommunication: useAxiosAsync(createPatientCommunication),
    replyToPatientCommunication: useAxiosAsync(replyToPatientCommunication),
  }
}

export enum PatientCommunicationStatus {
  COMPLETED = 'completed',
  RESTRICTED = 'restricted',
  AWAITING_REPLY = 'awaiting_reply',
  ALLOWED = 'allowed',
}

export function useCheckReplyStatus(patientCommunication?: PatientCommunication) {
  const axios = useIntegrationAxios()
  const member = useSubjectMember()

  const checkCanReplyFetcher = async (): Promise<PatientCommunicationStatus> => {
    if (
      !patientCommunication ||
      patientCommunication?.status === 'completed' ||
      patientCommunication?.status === 'cancelled' ||
      patientCommunication?.status === 'expired'
    ) {
      return PatientCommunicationStatus.COMPLETED
    }

    const channels = await axios
      .get<PatientCommunicationChannels>('/synops/get-communication-channels', {
        params: { synopsPatientId: member?.id },
      })
      .then((response) => response.data)
      .catch((error) => {
        console.error('Something went wrong while fetching the channels', error)

        return {
          patientId: member?.id,
          channels: [],
        } as PatientCommunicationChannels
      })

    // Determine latest messages by createdAt to avoid relying on array order
    const latestMessage = getLatestMessageFromList(patientCommunication.meta.messages || [])

    // Only allow reply if the last message was not from the patient
    if (latestMessage?.sender?.type === 'patient') {
      return PatientCommunicationStatus.AWAITING_REPLY
    }

    const questionChannel = channels?.channels.find((channel) => channel.type === 'question')

    const latestSender = getLatestNonPatientSenderFromList(patientCommunication.meta.messages || [])
    const latestSenderTyped = latestSender as
      | (PatientCommunicationTarget & {
          specialisationId?: number
          userId?: number
        })
      | undefined

    // General AZMM question, check if user has the question channel
    if (latestSenderTyped?.type !== 'patient' && latestSenderTyped?.specialisationId === undefined) {
      return isNil(questionChannel) ? PatientCommunicationStatus.RESTRICTED : PatientCommunicationStatus.ALLOWED
    }

    // Check if the specialisation is in the question channel
    const specialisation = questionChannel?.specialisations.find(
      (spec) => spec.id === (latestSenderTyped?.specialisationId as number),
    )

    // Specialisation Question (no preferred doctor)
    if (latestSenderTyped?.type !== 'patient' && latestSenderTyped?.userId === undefined) {
      return PatientCommunicationStatus.ALLOWED
    }

    const doctor = specialisation?.doctors?.find((doctor) => doctor.id === (latestSenderTyped?.userId as number))
    // Doctor found = allow reply, else don't
    return isNil(doctor) ? PatientCommunicationStatus.RESTRICTED : PatientCommunicationStatus.ALLOWED
  }

  return useSWR(`check-can-reply-communication-${patientCommunication?.id}`, checkCanReplyFetcher, {
    errorRetryCount: 3,
  })
}
